'use client';

import React from 'react';
import NextImage from 'next/image';
import { CldImage } from 'next-cloudinary';
import { useLocale } from 'next-intl';
import { getLocalizationInfo } from '@/project.config';
import { ImageProps } from './types';
import useDimensions from './hooks/useDimensions';
import defaultLoader from './loaders/default';
import { createMediaLoader } from './loaders/jaycar-media';

const Image = ({ media, ratio, gravity, suffix, src = '', width, height, title, alt = '', ...props }: ImageProps) => {
  const locale = useLocale();
  // Locale-bound loader: rewrites our media-CDN URLs to the active region's
  // Electus host (EWM-453). next/image loaders are pure fns with no locale, so
  // bind it here where `locale` is in scope.
  const mediaLoader = React.useMemo(() => createMediaLoader(locale), [locale]);

  const dimensions = useDimensions({ media, width, height, ...props });

  if (!media?.mediaId) {
    const resolvedSrc = defaultLoader({ src, suffix });
    if (!resolvedSrc) return null;

    // Centralized responsive defaults for raw-URL (product) images so callers
    // can pass just `src` and still get a correctly-sized image:
    //  • No explicit width/height → render with `fill`. This avoids the
    //    0-dimension trap where Next floors the request to its smallest width
    //    (~32px) and the CSS upscales it (the PDP blur). A filled image must sit
    //    in a position:relative/absolute parent with a defined size.
    //  • No explicit `sizes` → `sizes="auto"`, letting the browser measure the
    //    actual rendered box and pull the right srcset candidate — no per-call-site
    //    sizes needed. (`auto` applies to lazy images; eager/LCP images should
    //    still pass an explicit `sizes`.)
    // Callers may override either by passing `fill`/`width`/`height`/`sizes`.
    const { fill: fillProp, sizes: sizesProp, ...rest } = props;
    const fill = fillProp ?? (width == null && height == null);
    const sizes = sizesProp ?? (fill ? 'auto' : undefined);

    return (
      <NextImage
        src={resolvedSrc}
        loader={mediaLoader}
        alt={alt}
        title={title as string}
        {...(fill ? { fill: true } : { width, height })}
        {...(sizes ? { sizes } : {})}
        {...rest}
      />
    );
  }

  const mediaTitle =
    typeof title === 'string'
      ? title
      : (title?.[getLocalizationInfo(locale).locale.replace('-', '_') as keyof typeof title] ??
        Object.values(title ?? {})[0]);

  return (
    <CldImage
      src={media.mediaId}
      alt={mediaTitle || alt}
      title={mediaTitle}
      aspectRatio={ratio}
      gravity={gravity?.mode ?? 'auto'}
      format="auto"
      crop={{ type: 'scale', x: gravity?.coordinates?.x.toString(), y: gravity?.coordinates?.y.toString() }}
      quality="auto"
      {...dimensions}
      {...props}
    />
  );
};

export default Image;
